// We want to load Stripe.js loaded on all pages (https://github.com/stripe/stripe-js#ensuring-stripejs-is-available-everywhere)
// This uses "importing as a side-effect": https://github.com/stripe/stripe-js#import-as-a-side-effect
import "@stripe/stripe-js";

import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { EVENT_SOURCE } from "@outschool/experiments-shared";
import { LookupIPProvider } from "@outschool/iplookup-client";
import {
  BASE_LOCALE,
  SUPPORTED_LOCALES,
  SupportedLocale,
  TranslationsProvider,
  createUseMachineTranslationHook,
  createUseTranslationHook
} from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import { loginPath } from "@outschool/routes";
import * as Sentry from "@outschool/sentry/dist/client";
import {
  AnalyticsProvider,
  PageContextProvider,
  isBotRequest
} from "@outschool/ui-analytics";
import { ApolloProvider } from "@outschool/ui-apollo";
import { LoginFlowProvider, TokenProvider } from "@outschool/ui-auth";
import { FilestackProvider } from "@outschool/ui-components-classroom";
import { VideoPlayerProvider } from "@outschool/ui-components-shared";
import {
  PageAlertsProvider,
  TopNoticeProvider,
  useTopNoticeContext
} from "@outschool/ui-components-website";
import { ExperimentsServiceProvider } from "@outschool/ui-experiments";
import {
  ClientVersionProvider,
  RateLimitConfig,
  useClientVersion,
  useGraphqlClient
} from "@outschool/ui-gql";
import {
  GlobalStyle,
  Image,
  LegacyThemeProvider,
  Loading
} from "@outschool/ui-legacy-component-library";
import {
  ImageComponentProvider,
  IsBotProvider,
  NavigationProvider
} from "@outschool/ui-utils";
import { History } from "history";
import { i18n } from "i18next";
import React from "react";
import { hot } from "react-hot-loader/root";
import { Redirect, Route, Router, Switch } from "react-router-dom";

import {
  EXPERIMENTS_API_KEY,
  EXPERIMENTS_SERVICE_URL,
  EXPERIMENT_ASSIGNMENTS,
  EXPERIMENT_ASSIGNMENTS_MAX_REQ_TIME_MS,
  GOOGLE_TRANSLATE_CLIENT_KEY,
  GOOGLE_TRANSLATE_URL,
  IPLOOKUP_API_BASE_URL,
  IPLOOKUP_API_KEY,
  IPLOOKUP_INFO,
  OUTSCHOOL_ENV,
  SEARCH_LISTINGS_RATE_LIMIT_PER_MINUTE,
  clientVersion,
  isDevelopment,
  isPrerenderRequest,
  isProduction,
  isStaging,
  isTest
} from "../../shared/Env";
import useExperimentOverridesEnabled from "../hooks/useExperimentOverridesEnabled";
import { useIsTranslating } from "../hooks/useIsTranslating";
import usePageInfo from "../hooks/usePageInfo";
import { useWebsiteNavigation } from "../hooks/useWebsiteNavigation";
import clientSchema from "../lib/clientSchema";
import { FeatureFlagProvider } from "../lib/FeatureFlags";
import { AttributionProvider } from "../providers/AttributionProvider";
import { LocalizationProvider } from "../providers/LocalizationProvider";
import AppContainer from "../routes/AppContainer";
import ROUTES from "../routes/index";
import NotFound from "../routes/NotFound";
import AppState from "../stores/AppState";
import { AppStateProvider, useAppState } from "../stores/AppStateProvider";
import ExperimentSignupWithGoogleOneTap from "./ExperimentSignupWithGoogleOneTap";
import Link from "./Link";
import ReactFilestack from "./ReactFilestack";
import SimpleErrorBoundary from "./SimpleErrorBoundary";
import VideoPlayer from "./VideoPlayer";

const GRAPHQL_RATE_LIMITS: RateLimitConfig = {};
if (SEARCH_LISTINGS_RATE_LIMIT_PER_MINUTE) {
  GRAPHQL_RATE_LIMITS.SearchListings = {
    operationsPerMinute: SEARCH_LISTINGS_RATE_LIMIT_PER_MINUTE
  };
}

const emotionCache = createCache({
  key: "outschool",
  // We have to disable "speedy" mode for Prerender
  // See: https://github.com/prerender/prerender/issues/522
  speedy: (isProduction || isStaging) && !isPrerenderRequest
});

type RouteMapProps = {
  appState: any;
};

const RouteMap = React.memo(({ appState }: RouteMapProps) => (
  <Switch>
    {ROUTES.map(routeDefinition => {
      const { path, redirect } = routeDefinition;

      const originalPath = Array.isArray(path) ? path[0] : path;
      if (redirect) {
        return (
          <Redirect
            key={originalPath}
            exact
            from={originalPath}
            to={redirect}
          />
        );
      }

      const {
        component: Component,
        onEnter,
        exact = true,
        roles = []
      } = routeDefinition;

      return (
        <Route
          key={originalPath}
          exact={exact}
          path={path}
          render={renderProps => {
            const { location } = renderProps;

            const redirectToPath =
              onEnter &&
              appState[onEnter] &&
              appState[onEnter](location, roles);
            if (redirectToPath?.startsWith("http")) {
              // Some redirects are complete URLs outside of react-router, go directly to those.
              /* eslint-disable-next-line no-restricted-syntax */
              window.location.replace(redirectToPath);
              return null;
            }

            return redirectToPath ? (
              <Redirect exact to={redirectToPath} />
            ) : (
              // @ts-ignore TS(2604): JSX element type 'Component' does not have any con... Remove this comment to see the full error message
              <Component {...renderProps} />
            );
          }}
        />
      );
    })}
    <Route component={NotFound} />
  </Switch>
));

const OPTIONAL_ROUTE_LOCALE_PATH = `/:locale(${SUPPORTED_LOCALES.join(
  "|"
)})?/*`;

type LocalizedRouterProps = React.PropsWithChildren<{
  history: History;
  userSelectedLocale?: SupportedLocale;
}>;

const LocalizedRouter = ({
  history,
  userSelectedLocale,
  children
}: LocalizedRouterProps) => {
  const appState = useAppState();

  return (
    <Router history={history}>
      <ImageComponentProvider imageComponent={Image}>
        <NavigationProvider
          linkComponent={Link}
          useNavigation={useWebsiteNavigation}
        >
          <LoginFlowProvider setHandleLoggedIn={appState.setHandleLoggedIn}>
            <Route path={OPTIONAL_ROUTE_LOCALE_PATH} exact={false}>
              {({ location }) => {
                // react-router location will not have a basename (locale) in the pathname.
                // If this pathname does contain one, it means the URL contains two, duplicate, locales.
                // This occurs where we are programatically building a path using the window.location (such as login)
                // Remove this locale so we can properly redirect.
                const { pathname, search } = location;
                const [basePathname] = pathname.split("/").filter(Boolean);
                if (
                  userSelectedLocale &&
                  userSelectedLocale !== BASE_LOCALE &&
                  basePathname === userSelectedLocale
                ) {
                  const redirectPath = `${pathname.slice(
                    `/${basePathname}`.length
                  )}${search}`;
                  return <Redirect to={redirectPath} />;
                }

                return (
                  <AppContainer>
                    <LocalizationProvider
                      userSelectedLocale={userSelectedLocale}
                    >
                      {children}
                    </LocalizationProvider>
                  </AppContainer>
                );
              }}
            </Route>
          </LoginFlowProvider>
        </NavigationProvider>
      </ImageComponentProvider>
    </Router>
  );
};

const useTranslation = createUseTranslationHook(useIsTranslating);

type AppProps = {
  appState: AppState;
  userSelectedLocale?: SupportedLocale;
  i18n: i18n;
  history: History;
};

function WrappedInTokenContext({
  appState,
  userSelectedLocale,
  history
}: Omit<AppProps, "i18n">) {
  const isExperimentOverridesEnabled = useExperimentOverridesEnabled();
  const expectedClientVersionRef = useClientVersion();

  const apolloClient = useGraphqlClient({
    initialClientVersion: clientVersion,
    expectedClientVersionRef,
    userSelectedLocale,
    rateLimits: GRAPHQL_RATE_LIMITS,
    typeDefs: clientSchema
  });

  return (
    <ExperimentsServiceProvider
      initialExperimentAssignments={EXPERIMENT_ASSIGNMENTS}
      experimentsApiKey={EXPERIMENTS_API_KEY}
      // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
      experimentsServiceUrl={EXPERIMENTS_SERVICE_URL}
      isTest={isTest}
      isProduction={isProduction}
      isExperimentOverridesEnabled={isExperimentOverridesEnabled}
      osEnv={OUTSCHOOL_ENV}
      timeoutMs={EXPERIMENT_ASSIGNMENTS_MAX_REQ_TIME_MS}
      eventSource={EVENT_SOURCE.BROWSER}
      logException={Sentry.logException}
    >
      <ApolloProvider client={apolloClient}>
        <AppStateProvider appState={appState}>
          <FeatureFlagProvider>
            <LookupIPProvider
              // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
              iplookupApiBaseUrl={IPLOOKUP_API_BASE_URL}
              // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
              iplookupApiKey={IPLOOKUP_API_KEY}
              skip={isPrerenderRequest}
              bakedLocation={IPLOOKUP_INFO}
            >
              <VideoPlayerProvider videoPlayerComponent={VideoPlayer}>
                <FilestackProvider ReactFilestack={ReactFilestack}>
                  <React.Suspense fallback={<Loading delay={1000} />}>
                    <LocalizedRouter
                      history={history}
                      userSelectedLocale={userSelectedLocale}
                    >
                      <>
                        <RouteMap appState={appState} />
                        <ExperimentSignupWithGoogleOneTap />
                      </>
                    </LocalizedRouter>
                  </React.Suspense>
                </FilestackProvider>
              </VideoPlayerProvider>
            </LookupIPProvider>
          </FeatureFlagProvider>
        </AppStateProvider>
      </ApolloProvider>
    </ExperimentsServiceProvider>
  );
}

export function TranslationsInterstitial({
  appState,
  userSelectedLocale,
  i18n,
  history
}: AppProps) {
  const { setTopNotice } = useTopNoticeContext();

  const useMachineTranslation = React.useMemo(() => {
    return createUseMachineTranslationHook({
      // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
      googleTranslateClientKey: GOOGLE_TRANSLATE_CLIENT_KEY,
      // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
      googleTranslateUrl: GOOGLE_TRANSLATE_URL,
      useIsTranslating,
      onTranslated: () => setTopNotice(""),
      onError: ({ error, translatedErrorMessage }) => {
        setTopNotice(translatedErrorMessage);
        Sentry.logException(error, {
          tags: {
            component: Component.International,
            isGoogleTranslate: true
          }
        });
      }
    });
  }, [setTopNotice]);

  const onLogout = React.useCallback(() => {
    Sentry.configureScope((scope: any) => scope.setUser());
    window.Intercom?.("shutdown");

    // Full page reload on logout()
    //eslint-disable-next-line no-restricted-syntax
    window.location.assign(
      userSelectedLocale === BASE_LOCALE
        ? loginPath(true)
        : "/" + userSelectedLocale + loginPath(true)
    );
  }, [userSelectedLocale]);

  return (
    <TranslationsProvider
      i18n={i18n}
      useTranslation={useTranslation}
      useMachineTranslation={useMachineTranslation}
    >
      <SimpleErrorBoundary>
        <AttributionProvider>
          <TokenProvider
            isTest={isTest}
            isDevelopment={isDevelopment}
            onLogout={onLogout}
          >
            <WrappedInTokenContext
              appState={appState}
              userSelectedLocale={userSelectedLocale}
              history={history}
            />
          </TokenProvider>
        </AttributionProvider>
      </SimpleErrorBoundary>
    </TranslationsProvider>
  );
}

export default hot(
  ({ appState, userSelectedLocale, i18n, history }: AppProps) => {
    const pageInfo = usePageInfo(history, userSelectedLocale);
    const [isBot] = React.useState(() => isBotRequest());
    return (
      <PageContextProvider pageInfo={pageInfo}>
        <ClientVersionProvider initialClientVersion={clientVersion}>
          <LookupIPProvider
            // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
            iplookupApiBaseUrl={IPLOOKUP_API_BASE_URL}
            // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
            iplookupApiKey={IPLOOKUP_API_KEY}
            skip={isPrerenderRequest}
            bakedLocation={IPLOOKUP_INFO}
          >
            <IsBotProvider value={isPrerenderRequest || isBot}>
              <AnalyticsProvider>
                <CacheProvider value={emotionCache}>
                  <LegacyThemeProvider>
                    <PageAlertsProvider>
                      <TopNoticeProvider>
                        <TranslationsInterstitial
                          appState={appState}
                          userSelectedLocale={userSelectedLocale}
                          i18n={i18n}
                          history={history}
                        />
                      </TopNoticeProvider>
                    </PageAlertsProvider>
                  </LegacyThemeProvider>
                  <GlobalStyle />
                </CacheProvider>
              </AnalyticsProvider>
            </IsBotProvider>
          </LookupIPProvider>
        </ClientVersionProvider>
      </PageContextProvider>
    );
  }
);
